import React, { FunctionComponent, PropsWithChildren, useMemo } from 'react';
import * as classes from './Stack.module.scss';
import { mergeClasses } from '../../util/style';

type FlexAlign =
    'normal'
    | 'stretch'
    | 'baseline'
    | 'center'
    | 'start'
    | 'end'
    | 'self-start'
    | 'self-end'
    | 'flex-start'
    | 'flex-end'

type FlexJustify =
    'flex-start'
    | 'flex-end'
    | 'center'
    | 'space-between'
    | 'space-around'

type FlexWrap =
    'wrap'
    | 'nowrap'
    | 'reverse-wrap'

type StackStyles = {
    '--stack-grow'?: number,
    '--stack-shrink'?: number,
    '--stack-order'?: number,
    '--stack-align'?: FlexAlign,
    '--stack-justify'?: FlexJustify,
    '--stack-wrap'?: FlexWrap,
}

interface IStackProps {
    horizontal?: boolean;
    reverse?: boolean;
    grow?: number | boolean;
    shrink?: number | boolean;
    order?: number;
    fill?: boolean;
    align?: FlexAlign;
    justify?: FlexJustify;
    wrap?: FlexWrap;
    className?: string;
}

export const Stack: FunctionComponent<PropsWithChildren<IStackProps>> = (props) => {
    const styles = useMemo(() => {
        const result: StackStyles = {};

        if (props.grow) {
            result['--stack-grow'] = Number(props.grow);
        }

        if (props.shrink) {
            result['--stack-shrink'] = Number(props.shrink);
        }

        if (props.order !== undefined) {
            result['--stack-order'] = props.order;
        }

        if (props.align !== undefined) {
            result['--stack-align'] = props.align;
        }

        if (props.justify !== undefined) {
            result['--stack-justify'] = props.justify;
        }

        if (props.wrap !== undefined) {
            result['--stack-wrap'] = props.wrap;
        }

        return result as React.CSSProperties;
    }, [ props.grow, props.shrink, props.order, props.align, props.justify, props.wrap ]);

    const stackClasses = mergeClasses(
        classes.stack,
        props.horizontal && classes.horizontal,
        props.reverse && classes.reverse,
        !!props.grow && classes.grow,
        !!props.shrink && classes.shrink,
        (props.order !== undefined) && classes.ordered,
        (props.align !== undefined) && classes.aligned,
        (props.justify !== undefined) && classes.justified,
        (props.wrap !== undefined) && classes.wrap,
        props.fill && classes.fill,
        props.className,
    );

    return <div className={stackClasses} style={styles}>
        {props.children}
    </div>;
};
